import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, UtensilsCrossed, ShoppingBag, Truck } from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import MenuItemCard from "@/components/shared/MenuItemCard";
import ItemDetailModal from "@/components/shared/ItemDetailModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicMenu, MenuItem } from "@/hooks/usePublicMenu";
import { useSessionContext } from "@/contexts/SessionContext";
import { promotions } from "@/data/mockData";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

export default function ClassicHome() {
  const { t, language } = useLanguage();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const { session } = useSessionContext();
  const { menuItems, loading } = usePublicMenu(session.order.branchId);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const popularItems = menuItems.filter((item) => item.popular);

  const orderTypes = [
    { icon: UtensilsCrossed, label: t("order.dineIn"), desc: t("order.dineInDesc") },
    { icon: ShoppingBag, label: t("order.takeaway"), desc: t("order.takeawayDesc") },
    { icon: Truck, label: t("order.delivery"), desc: t("order.deliveryDesc") },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 py-16 md:py-24">
            <div className="max-w-2xl">
              <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-foreground leading-tight">
                {t("hero.title")} <span className="text-primary">{brand.logoType === "image" ? <img src={brand.logo} alt="" className="inline h-12 md:h-16" /> : brand.logo}</span>
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-lg">
                {t("hero.subtitle")}
              </p>
              <Button
                size="lg"
                className="mt-8 text-base font-bold gap-2 px-8 py-6 rounded-xl"
                onClick={() => navigate("/menu")}
              >
                {t("hero.cta")}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="absolute -top-20 -end-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-20 -start-20 h-64 w-64 rounded-full bg-secondary/10 blur-3xl" />
        </section>

        {/* Order Type Selector */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-center text-foreground mb-8">
            {t("order.type")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {orderTypes.map(({ icon: Icon, label, desc }) => (
              <button
                key={label}
                onClick={() => navigate("/menu")}
                className="group flex flex-col items-center gap-3 rounded-xl border-2 border-border bg-card p-6 text-center transition-all duration-200 hover:border-primary hover:shadow-lg"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="font-heading font-bold text-foreground">{label}</h3>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Featured Items */}
        <section className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              {t("featured.title")} 🔥
            </h2>
            <Button variant="ghost" className="gap-1" onClick={() => navigate("/menu")}>
              {t("nav.menu")}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-xl border bg-card animate-pulse aspect-[4/5]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {popularItems.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  onClick={setSelectedItem}
                />
              ))}
            </div>
          )}
        </section>

        {/* Promotions */}
        <section className="container mx-auto px-4 py-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-6">
            {t("promos.title")} 🎉
          </h2>
          <Carousel className="mx-auto max-w-4xl">
            <CarouselContent>
              {promotions.map((promo) => (
                <CarouselItem key={promo.id}>
                  <div className="relative overflow-hidden rounded-xl">
                    <img
                      src={promo.image}
                      alt={language === "ar" ? promo.titleAr : promo.title}
                      className="w-full h-48 md:h-64 object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent flex flex-col justify-end p-6">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-primary-foreground">
                        {language === "ar" ? promo.titleAr : promo.title}
                      </h3>
                      <p className="text-sm text-primary-foreground/80 mt-1">
                        {language === "ar" ? promo.descriptionAr : promo.description}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-start-4 md:-start-12" />
            <CarouselNext className="-end-4 md:-end-12" />
          </Carousel>
        </section>
      </main>

      <Footer />

      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}
